import Typography from "@mui/material/Typography";

import CheckoutLineItem from "@app/analytics/account/Billing/components/CheckoutLineItem";
import ExitCheckoutButton from "@app/analytics/account/Billing/components/ExitCheckoutButton";
import type { CreditPackage } from "@app/analytics/account/Billing/types";
import ROUTES from "@app/common/routes";
import type { PreviewInvoice } from "@app/common/types";

import Stack, { Row } from "@rui/Stack";

import { BreakdownHighlight, LineItemDivider } from "../styles";

interface BillingCreditCheckoutBreakdownProps {
  invoice: PreviewInvoice | null;
  isLoading: boolean;
  selectedPackage: CreditPackage;
}

const fromCents = (cents: number | null) => (cents ?? 0) / 100;

const BillingCreditCheckoutBreakdown = ({
  invoice,
  isLoading,
  selectedPackage,
}: BillingCreditCheckoutBreakdownProps) => {
  const credits = invoice
    ? selectedPackage.price + fromCents(invoice.tax) - fromCents(invoice.total)
    : 0;

  return (
    <Stack variant="center" flex={1} sx={{ width: "100%", p: 2 }}>
      <Stack
        variant="left"
        sx={{ width: "100%", maxWidth: "480px" }}
        spacing={1.5}
      >
        <ExitCheckoutButton to={ROUTES.BILLING}>
          Plan & Billing
        </ExitCheckoutButton>
        <Stack spacing={0.5} sx={{ pb: 1 }}>
          <Row variant="left" spacing={1}>
            <Typography variant="h1">Upload minutes</Typography>
          </Row>
          <Typography
            variant="bodySecondaryMed"
            sx={{ color: "transparent.white.60" }}
          >
            Process your own recordings in Read to generate a report
          </Typography>
        </Stack>
        <BreakdownHighlight spacing={0.5}>
          <CheckoutLineItem
            label={`${selectedPackage.quantity} minutes`}
            value={selectedPackage.price}
          />
        </BreakdownHighlight>
        <Stack sx={{ p: 2, width: "100%" }} spacing={1}>
          <CheckoutLineItem label="Subtotal" value={selectedPackage.price} />
          <CheckoutLineItem
            label="Credits"
            isLoading={isLoading}
            value={invoice ? -credits : undefined}
            zeroValue="-"
          />
          <LineItemDivider />
          <CheckoutLineItem
            testId="billing-sales-tax"
            label="Sales tax"
            isLoading={isLoading}
            unit="cents"
            value={invoice ? invoice.tax ?? 0 : undefined}
          />
          <LineItemDivider />
          <CheckoutLineItem
            label="Total"
            isLoading={isLoading}
            unit="cents"
            value={invoice?.total}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default BillingCreditCheckoutBreakdown;
